import { useLocation, useNavigate } from 'react-router-dom'
import { Eye, Sparkles } from 'lucide-react'
import { Button } from '@/shared/ui'
import { usePreviewSession } from '../hooks/use-preview-session'

const CLAIM_PATH = '/settings'
const CLAIM_TAB = 'subscription'

/**
 * Sticky top banner shown to anyone signed in via a redeemed preview
 * token. Renders nothing for normal staff/admin sessions. Stays mounted
 * for the entire app lifetime so the user is never out of context about
 * which mode they're in.
 *
 * The "Reclamar" CTA navigates to the Subscription tab where
 * `ClaimClubCard` lives — a single inline form makes the trial decision
 * in context (status, plan, wipe consequence). When the visitor is
 * already on that screen, the CTA hides so we don't double-affordance.
 */
export function PreviewBanner() {
  const session = usePreviewSession()
  const navigate = useNavigate()
  const location = useLocation()
  if (!session.active || !session.clubId) return null

  const params = new URLSearchParams(location.search)
  const onClaimScreen =
    location.pathname === CLAIM_PATH && params.get('tab') === CLAIM_TAB
  const goToClaim = () => navigate(`${CLAIM_PATH}?tab=${CLAIM_TAB}`)

  return (
    <div className="preview-banner" role="status">
      <div className="preview-banner-text">
        <Eye size={14} aria-hidden="true" />
        <span>
          <strong>Modo demostración</strong>
          {!session.isPublicDemo && session.daysLeft != null && (
            <>
              {' · '}
              <span>
                Quedan {session.daysLeft}{' '}
                {session.daysLeft === 1 ? 'día' : 'días'}
              </span>
            </>
          )}
          {' · '}
          <span className="faint">
            {session.isPublicDemo
              ? 'Datos ficticios · solo lectura · actualización semanal.'
              : 'Explora tu club; reclámalo para activar la prueba.'}
          </span>
        </span>
      </div>
      {session.isPublicDemo ? (
        <Button
          variant="primary"
          size="sm"
          icon={Sparkles}
          onClick={() => window.open('https://upadelmatch.com/clubes/#contact-form', '_blank', 'noopener,noreferrer')}
          className="preview-allowed"
        >
          Crear mi club
        </Button>
      ) : !onClaimScreen && (
        <Button
          variant="primary"
          size="sm"
          icon={Sparkles}
          onClick={goToClaim}
          // Stays clickable while body.preview-mode dims every other
          // primary/danger button — see index.css `.preview-allowed`.
          className="preview-allowed"
        >
          Reclamar
        </Button>
      )}
    </div>
  )
}
